import { Fragment } from 'react';
import { CurrencyIcon } from 'src/modules/currencies/components/CurrencyIcon';
import { useCurrencyBySymbol } from 'src/modules/currencies/hooks/useCurrencyBySymbol';
import { getKnownCurrencyLogo } from 'src/modules/currencies/utils/knownCurrencyLogos';
import { formatUnits } from 'viem';

import { Text } from '@/design-system/components/ui';
import { formatDecimalAmount, formatToCompactNumber } from '@/utils/format';

import { type TokenAmount } from '../domain/types';

/**
 * A `TokenAmount` that may carry its own decimals. Endpoints that return the
 * currency's decimals per row (FUU-2085's `/users`) should pass them so the
 * amount is scaled from the row's own data instead of a `useCurrencyBySymbol`
 * name lookup, which silently falls back to "0 decimals" on a miss and renders
 * raw base units (FUU-1999).
 *
 * `null` means "this currency has no decimals" (POINT) and is also treated as 0
 * — consistent with AdminClaimsTable.tsx:64 and FormattedAmount.tsx:189.
 */
export interface EarningAmount extends TokenAmount {
  decimals?: number | null;
}

export interface MultiCurrencyEarningsProps {
  earnings: EarningAmount[];
  compact?: boolean;
  size?: 'sm' | 'lg';
  scaleByDecimals?: boolean;
  /** Renders each amount with its token icon (FUU-1999). Off by default. */
  showIcons?: boolean;
  iconSize?: number;
  /**
   * `compact` (default) abbreviates — `27.78` reads as `28`. `decimal` keeps the
   * amount's own precision via `formatDecimalAmount`, which is what the All
   * Users table has always shown for earnings.
   */
  numberFormat?: 'compact' | 'decimal';
  /**
   * Chain to resolve symbols on (`project.contractChainId`, the identifier
   * PaymentCurrencySelector.tsx:34 already passes). Without it a symbol lookup
   * can return the same-named currency of another chain — a wrong logo, and
   * wrong decimals on the lookup fallback path.
   */
  chainIdentifier?: string | null;
}

type NumberFormatter = (value: number) => string;

const NUMBER_FORMATTERS: Record<'compact' | 'decimal', NumberFormatter> = {
  compact: formatToCompactNumber,
  decimal: formatDecimalAmount,
};

function scaleAmount(amount: number, decimals: number | undefined): number {
  if (decimals === undefined) return amount;
  return Number(formatUnits(BigInt(Math.trunc(amount)), decimals));
}

/** `undefined` = "unknown, go look it up"; `null` = "known to have no decimals". */
function hasOwnDecimals(tokenAmount: EarningAmount): boolean {
  return tokenAmount.decimals !== undefined;
}

interface TokenEarningTextProps {
  tokenAmount: EarningAmount;
  formatNumber: NumberFormatter;
  chainIdentifier?: string | null;
}

function ScaledTokenEarningText({ tokenAmount, formatNumber, chainIdentifier }: TokenEarningTextProps) {
  // The decimals lookup is only needed when the row didn't bring its own. The
  // icon beside it may still need the same query (see TokenEarningIcon), so
  // disabling it here does not always avoid the request — it just means the text
  // never depends on it.
  const { data: currency } = useCurrencyBySymbol(tokenAmount.currency, chainIdentifier, !hasOwnDecimals(tokenAmount));
  const decimals = hasOwnDecimals(tokenAmount) ? (tokenAmount.decimals ?? 0) : currency?.decimals;
  const scaled = scaleAmount(tokenAmount.amount, decimals);
  return (
    <>
      {formatNumber(scaled)} {tokenAmount.currency}
    </>
  );
}

function PlainTokenEarningText({ tokenAmount, formatNumber }: TokenEarningTextProps) {
  return (
    <>
      {formatNumber(tokenAmount.amount)} {tokenAmount.currency}
    </>
  );
}

/**
 * Icon + amount, laid out like `FixedAmount`
 * (common/components/cards/PayoutTermCard/FormattedAmount/FormattedAmount.tsx:196-208).
 *
 * Symbols `getKnownCurrencyLogo` already covers (POINT, USDC, USDT, ETH, WETH,
 * BTC, XLM — knownCurrencyLogos.ts:1-13) render straight from that map, so no
 * currency row and no request is needed; `CurrencyIcon` would fall back to the
 * very same asset anyway (CurrencyIcon.tsx:12). Anything else is looked up on
 * the project's own chain, so a same-named currency on another chain can't win.
 */
function TokenEarningIcon({
  tokenAmount,
  size,
  chainIdentifier,
}: {
  tokenAmount: EarningAmount;
  size: number;
  chainIdentifier?: string | null;
}) {
  const knownLogoUri = getKnownCurrencyLogo(tokenAmount.currency);
  const { data: currency } = useCurrencyBySymbol(tokenAmount.currency, chainIdentifier, !knownLogoUri);
  return <CurrencyIcon logoUri={knownLogoUri ?? currency?.logoUri} name={tokenAmount.currency} size={size} />;
}

export function MultiCurrencyEarnings({
  earnings,
  compact,
  size = 'sm',
  scaleByDecimals = false,
  showIcons = false,
  iconSize = 16,
  numberFormat = 'compact',
  chainIdentifier,
}: MultiCurrencyEarningsProps) {
  const variant = size === 'lg' ? (earnings.length > 1 ? 'body-md' : 'title-md') : 'body-sm';
  const EntryText = scaleByDecimals ? ScaledTokenEarningText : PlainTokenEarningText;
  const formatNumber = NUMBER_FORMATTERS[numberFormat];

  if (earnings.length === 0) {
    return (
      <Text variant={variant} color="muted">
        —
      </Text>
    );
  }

  if (showIcons) {
    // Icons force a flex layout, so `compact` becomes "one row" rather than
    // "one comma-joined string".
    return (
      <div
        className={
          compact ? 'tw:flex tw:flex-wrap tw:items-center tw:gap-x-3 tw:gap-y-1' : 'tw:flex tw:flex-col tw:gap-1'
        }
      >
        {earnings.map((e, i) => (
          <span key={`${e.currency}-${i}`} className="tw:flex tw:items-center tw:gap-[0.3rem]">
            <TokenEarningIcon tokenAmount={e} size={iconSize} chainIdentifier={chainIdentifier} />
            <Text variant={variant}>
              <EntryText tokenAmount={e} formatNumber={formatNumber} chainIdentifier={chainIdentifier} />
            </Text>
          </span>
        ))}
      </div>
    );
  }

  if (compact) {
    return (
      <Text variant={variant}>
        {earnings.map((e, i) => (
          <Fragment key={`${e.currency}-${i}`}>
            {i > 0 && ', '}
            <EntryText tokenAmount={e} formatNumber={formatNumber} chainIdentifier={chainIdentifier} />
          </Fragment>
        ))}
      </Text>
    );
  }

  return (
    <div className="tw:flex tw:flex-col tw:gap-1">
      {earnings.map((e) => (
        <Text key={e.currency} variant={variant}>
          <EntryText tokenAmount={e} formatNumber={formatNumber} chainIdentifier={chainIdentifier} />
        </Text>
      ))}
    </div>
  );
}
